"use client";

import Link from "next/link";
import Image from "next/image";
import { ArrowRight } from "lucide-react";

export default function AboutUsStory() {
  return (
    <>
      <div className="footer-top-accent" aria-hidden="true" />
      <section className="py-10! bg-background-primary relative overflow-hidden">
        <div className="container max-w-7xl mx-auto! px-6! relative z-10">
          <div className="flex flex-col lg:flex-row items-center lg:items-stretch gap-12 lg:gap-16">
            <div className="flex-1 flex flex-col justify-center max-w-2xl">
              <p className="text-sm font-bold tracking-[0.2em] uppercase text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600 dark:from-purple-500 dark:to-blue-500 mb-4">
                Our Story
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-text-primary mb-6! leading-[1.1]">
                Built with passion <br /> since{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-500 dark:to-indigo-500">2016</span>
              </h2>
              <div className="w-16 h-1 bg-gradient-to-r from-purple-600 to-blue-600 dark:from-purple-500 dark:to-blue-500 mb-8! rounded-full"></div>
              <p className="text-text-secondary text-base leading-relaxed mb-6! font-light">
                Tycho Technologies has been delivering innovative web and mobile development solutions since 2016, helping businesses build strong digital foundations and scalable
                technology platforms. Our focus has always been on creating reliable, user-centric, and performance-driven digital experiences tailored to modern business needs.
              </p>
              <p className="text-text-secondary text-base leading-relaxed mb-10! font-light">
                With expertise in web development, mobile applications, frontend technologies, and enterprise solutions, we help startups, growing businesses, and enterprises
                transform ideas into impactful digital products. Quality, transparency, and long-term client success remain at the core of everything we build.
              </p>
              <p className="text-text-secondary text-base leading-relaxed mb-10! font-light">
                Our experienced team combines technical expertise, creative problem-solving, and industry knowledge to deliver solutions that align with evolving market demands.
                From strategy and design to development and deployment, we maintain a practical and growth-focused approach throughout every project lifecycle.
              </p>
              <p className="text-text-secondary text-base leading-relaxed mb-10! font-light">
                Headquartered in Noida, India, with a growing presence across the UK and the US, Tycho Technologies continues to support global clients with scalable, secure, and
                future-ready digital solutions across multiple industries.
              </p>

              <Link
                href="/success-stories"
                className="self-start px-8! py-3.5! bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-medium rounded-xl flex items-center gap-3 transition-transform hover:scale-105 shadow-[0_0_20px_rgba(147,51,234,0.4)] group cursor-pointer"
              >
                Our Journey
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="w-full lg:w-[250px] flex-shrink-0 relative lg:left-[210px] z-50 mt-10! lg:mt-0!">
              <div
                className="h-full max-h-[600px] border border-border-primary backdrop-blur-xl rounded-3xl p-8! relative shadow-2xl flex flex-col justify-center"
                style={{
                  background: "var(--card-background)",
                  boxShadow: "var(--card-shadow)",
                }}
              >
                <div className="flex flex-col gap-10 relative z-10">
                  <div className="relative flex gap-6 items-start group">
                    <div className="absolute left-[6px] top-[24px] w-[2px] h-[calc(100%+18px)] bg-gradient-to-b from-purple-500 to-blue-500 opacity-60"></div>
                    <div className="w-3.5 h-3.5 rounded-full bg-white shadow-[0_0_15px_3px_rgba(168,85,247,0.8)] border-2 border-purple-500 mt-1.5! relative z-10 shrink-0 group-hover:scale-125 transition-transform"></div>
                    <div>
                      <h4 className="text-text-primary font-bold text-xl mb-2!">2016</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">Started delivering web & mobile solutions.</p>
                    </div>
                  </div>

                  <div className="relative flex gap-6 items-start group">
                    <div className="absolute left-[6px] top-[24px] w-[2px] h-[calc(100%+18px)] bg-gradient-to-b from-blue-500 to-emerald-400 opacity-60"></div>
                    <div className="w-3.5 h-3.5 rounded-full bg-white shadow-[0_0_15px_3px_rgba(59,130,246,0.8)] border-2 border-blue-500 mt-1.5! relative z-10 shrink-0 group-hover:scale-125 transition-transform"></div>
                    <div>
                      <h4 className="text-text-primary font-bold text-xl mb-2!">2020</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">Expanded our development expertise.</p>
                    </div>
                  </div>

                  <div className="relative flex gap-6 items-start group">
                    <div className="absolute left-[6px] top-[24px] w-[2px] h-[calc(100%+18px)] bg-gradient-to-b from-emerald-400 to-pink-500 opacity-60"></div>
                    <div className="w-3.5 h-3.5 rounded-full bg-white shadow-[0_0_15px_3px_rgba(52,211,153,0.8)] border-2 border-emerald-400 mt-1.5! relative z-10 shrink-0 group-hover:scale-125 transition-transform"></div>
                    <div>
                      <h4 className="text-text-primary font-bold text-xl mb-2!">2024</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">Serving clients across global markets.</p>
                    </div>
                  </div>

                  <div className="relative flex gap-6 items-start group">
                    <div className="w-3.5 h-3.5 rounded-full bg-white shadow-[0_0_15px_3px_rgba(236,72,153,0.8)] border-2 border-pink-500 mt-1.5! relative z-10 shrink-0 group-hover:scale-125 transition-transform"></div>
                    <div>
                      <h4 className="text-text-primary font-bold text-xl mb-2!">Present</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">Building future-ready digital products.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 lg:flex-[1.2] w-full lg:w-auto relative min-h-[450px] max-h-[600px] lg:min-h-full rounded-3xl overflow-hidden border border-border-primary group shadow-2xl mt-12! lg:mt-0!">
              <Image
                src="/images/tycho_building.webp"
                alt="Our Story Office"
                fill
                className="absolute inset-0 object-cover object-left transform group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-background-primary/95 via-transparent to-transparent hidden dark:block"></div>
              <div className="absolute inset-0 border-2 border-blue-500/10 rounded-3xl mix-blend-overlay pointer-events-none shadow-[inset_0_0_40px_rgba(59,130,246,0.2)]"></div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
